<template>
  <v-dialog v-model="dialog" max-width="400" persistent>
    <v-card class="ltcs-request-modal">
      <button
        type="button"
        class="ltcs-request-modal__close"
        aria-label="닫기"
        :disabled="isRequesting"
        @click="closeModal"
      >
        <v-icon size="18">mdi-close</v-icon>
      </button>
      <v-card-text class="modal-body">
        <div class="confirm-icon">
          <v-icon size="48" color="#1e3a5f">mdi-cloud-download</v-icon>
        </div>
        <div class="confirm-title">일정계획 데이터 요청</div>
        <div class="confirm-params">
          <span class="param">{{ displayYearMonth }}</span>
          <span v-if="selectedYearMonth" class="ym-code">{{ selectedYearMonth }}</span>
          <span class="divider">·</span>
          <span class="param">{{ bizGubun }}</span>
        </div>
        <div class="confirm-desc">
          위 연월({{ selectedYearMonth || '-' }})·사업구분으로 롱텀케어에서 데이터를 가져옵니다
        </div>

        <div class="status-message requesting" v-if="isRequesting">
          <v-progress-circular indeterminate size="16" width="2" />
          <span>요청 중...</span>
        </div>
        <div class="status-message" v-else-if="requestStatus" :class="requestStatus">
          <v-icon size="16">{{ statusIcon }}</v-icon>
          <span>{{ statusMessage }}</span>
        </div>
      </v-card-text>

      <v-card-actions class="modal-actions">
        <v-btn variant="text" class="btn-cancel" @click="closeModal" :disabled="isRequesting">
          취소
        </v-btn>
        <v-btn
          variant="flat"
          class="btn-register"
          :loading="isRequesting"
          @click="submitRequest"
        >
          요청
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, inject, type Ref } from 'vue';
import { useStore } from 'vuex';
import api from '@/services/api';
import { formatApiErrorMessage } from '@/utils/formatUserFacingError';

const props = defineProps<{
  modelValue: boolean;
  defaultYearMonth?: string;
  memberCode?: number | string;
  bizGubun?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'requested'): void;
}>();

const store = useStore();
const isAdmin = computed(
  () => Number(store.getters.actualRoleLevel ?? store.getters.roleLevel ?? 0) >= 60
);

// MemberDetailLayout에서 provide한 값들
const injectedMemberData = inject<Ref<any>>('memberData', ref(null));
const injectedBizGubun = inject<Ref<string>>('selectedBizGubun', ref(''));

// props > inject > store 순으로 fallback
const memberCode = computed(() => 
  props.memberCode || injectedMemberData.value?.m_code || store.state.user?.m_code
);
const bizGubun = computed(() => 
  props.bizGubun || injectedBizGubun.value || store.state.user?.m_biz_gubun || ''
);

const dialog = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const selectedYearMonth = ref('');
const isRequesting = ref(false);
const requestStatus = ref<'success' | 'error' | ''>('');
const statusMessage = ref('');

const statusIcon = computed(() => {
  switch (requestStatus.value) {
    case 'success': return 'mdi-check-circle';
    case 'error': return 'mdi-alert-circle';
    default: return '';
  }
});

const displayYearMonth = computed(() => {
  if (!selectedYearMonth.value || selectedYearMonth.value.length !== 6) return '-';
  const y = selectedYearMonth.value.substring(0, 4);
  const m = parseInt(selectedYearMonth.value.substring(4, 6));
  return `${y}년 ${m}월`;
});

watch(() => props.modelValue, (val) => {
  if (val) {
    requestStatus.value = '';
    statusMessage.value = '';
    if (props.defaultYearMonth) {
      selectedYearMonth.value = props.defaultYearMonth;
    } else {
      const now = new Date();
      selectedYearMonth.value = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
    }
  }
});

const closeModal = () => {
  dialog.value = false;
};

const submitRequest = async () => {
  if (!memberCode.value || !bizGubun.value) {
    requestStatus.value = 'error';
    statusMessage.value = isAdmin.value
      ? '기관 정보(m_code)를 불러올 수 없습니다.'
      : '기관 정보를 불러올 수 없습니다.';
    return;
  }

  isRequesting.value = true;
  requestStatus.value = '';

  try {
    await api.post('/macro/request', {
      m_code: memberCode.value,
      cmq_type: 'caion_ltcs_schedule_plan',
      params: {
        m_code: memberCode.value,
        yearMonth: selectedYearMonth.value,
        bizGubun: bizGubun.value
      }
    });
    requestStatus.value = 'success';
    statusMessage.value = '요청 완료!';
    isRequesting.value = false;
    
    setTimeout(() => {
      closeModal();
      if (confirm('일정계획 요청이 등록되었습니다.\n데이터 반영까지 약간의 시간이 소요됩니다.\n\n페이지를 새로고침하시겠습니까?')) {
        window.location.reload();
      }
      emit('requested');
    }, 500);
    return;
  } catch (error: any) {
    requestStatus.value = 'error';
    statusMessage.value = formatApiErrorMessage(error, {
      verbose: isAdmin.value,
      fallback: '요청에 실패했습니다. 잠시 후 다시 시도해 주세요.',
    });
  } finally {
    isRequesting.value = false;
  }
};
</script>

<style lang="scss">
.ltcs-request-modal {
  position: relative;

  // 우상단 닫기(X) — 제목이 본문 중앙에 있는 구조라 카드 기준 절대 배치
  .ltcs-request-modal__close {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #64748b;
    cursor: pointer;
    transition: background-color 0.15s, color 0.15s;

    &:hover:not(:disabled) {
      background: #e2e8f0;
      color: #1e293b;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .modal-body {
    padding: 32px 24px 16px;
    text-align: center;

    .confirm-icon {
      margin-bottom: 16px;
    }

    .confirm-title {
      font-size: 18px;
      font-weight: 600;
      color: #1e3a5f;
      margin-bottom: 8px;
    }

    .confirm-params {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 8px;

      .param {
        font-size: 15px;
        font-weight: 500;
        color: #334155;
      }

      .ym-code {
        padding: 2px 8px;
        border-radius: 4px;
        background: #1e3a5f;
        color: #fff;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.04em;
      }

      .divider {
        color: #94a3b8;
      }
    }

    .confirm-desc {
      font-size: 13px;
      color: #64748b;
    }

    .status-message {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin-top: 16px;
      padding: 10px;
      border-radius: 6px;
      font-size: 13px;

      &.requesting {
        background: #eff6ff;
        color: #1e3a5f;
      }

      &.success {
        background: #ecfdf5;
        color: #059669;
      }

      &.error {
        background: #fef2f2;
        color: #dc2626;
      }
    }
  }

  .modal-actions {
    padding: 12px 24px 20px;
    justify-content: center;
    gap: 12px;

    .v-btn {
      min-width: 80px;
    }
  }
}
</style>
